import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../middleware/api';
import { END_POINTS } from '../../utils/constants/endPoints';
import ValidationErrorToast from '../../components/validationErrorToast';
import InterviewSidebar from './InterviewSidebar';
import { styles } from './interviewStyles';

const InterviewResults: React.FC = () => {
  const [results, setResults] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(END_POINTS.interviewResults);
        setResults(Array.isArray(r) ? r : (r?.results || []));
      } catch (e: any) { ValidationErrorToast('Error', e?.message || 'Load failed'); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) {
    return (
      <InterviewSidebar>
        <div style={styles.emptyState}>
          <div className="spinner-border" role="status" style={{ color: '#0065FF' }}>
            <span className="sr-only">Loading...</span>
          </div>
          <p style={{ marginTop: '20px', ...styles.secondaryText }}>Loading results...</p>
        </div>
      </InterviewSidebar>
    );
  }

  const formatRating = (rating: any) => {
    if (rating == null || rating === '') return { text: 'Pending', color: '#6c757d', textColor: '#fff' };
    const str = String(rating).toUpperCase();
    if (str === 'READY') return { text: 'Ready', color: '#28a745', textColor: '#fff' };
    if (str === 'NOT_READY') return { text: 'Not Ready', color: '#dc3545', textColor: '#fff' };
    if (str === 'NEEDS_IMPROVEMENT') return { text: 'Needs Improvement', color: '#ffc107', textColor: '#212529' };
    const num = parseFloat(rating);
    if (!isNaN(num)) {
      const color = num > 7 ? '#28a745' : num > 4 ? '#ffc107' : '#dc3545';
      const textColor = (num > 4 && num <= 7) ? '#212529' : '#fff';
      return { text: `${num}/10`, color, textColor };
    }
    return { text: String(rating), color: '#6c757d', textColor: '#fff' };
  };

  return (
    <InterviewSidebar>
      <div style={styles.card}>
        <h3 style={styles.cardTitle}>Interview Results ({results.length})</h3>
        {results.length === 0 ? (
          <div style={{ ...styles.emptyState, minHeight: '300px' }}>
            <p style={styles.secondaryText}>No interview results yet. Results will appear here after candidates complete interviews.</p>
          </div>
        ) : (
          <div className="table-responsive">
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={{ ...styles.tableHeader, width: '25%' }}>Candidate</th>
                  <th style={{ ...styles.tableHeader, width: '25%' }}>Job</th>
                  <th style={{ ...styles.tableHeader, width: '15%', textAlign: 'center' }}>Rating</th>
                  <th style={{ ...styles.tableHeader, width: '20%' }}>Summary</th>
                  <th style={{ ...styles.tableHeader, width: '15%', textAlign: 'center' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {results.map((r: any) => (
                  <tr key={r.id}>
                    <td style={styles.tableCell}>
                      <strong style={styles.primaryText}>{r.candidateName || r.candidate_name || r.candidateEmail || r.candidate_email || '—'}</strong>
                    </td>
                    <td style={styles.tableCell}>{r.jobTitle || r.job_title || '—'}</td>
                    <td style={{ ...styles.tableCell, textAlign: 'center' }}>
                      {(() => { const rt = formatRating(r.rating); return (
                        <span style={{ ...styles.badge, backgroundColor: rt.color, color: rt.textColor }}>
                          {rt.text}
                        </span>
                      ); })()}
                    </td>
                    <td style={styles.tableCell}>
                      {(r.summary || r.monologue || '')?.slice(0, 60)}{(r.summary || r.monologue || '')?.length > 60 ? '…' : ''}
                    </td>
                    <td style={{ ...styles.tableCell, textAlign: 'center' }}>
                      <Link
                        to={`/interview-simulator/results/${r.id}`}
                        style={{ ...styles.button, ...styles.secondaryButton, textDecoration: 'none', padding: '6px 12px', fontSize: '13px' } as any}
                      >
                        View Detail
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </InterviewSidebar>
  );
};

export default InterviewResults;
